import {
  RoutePackageDTO,
  RoutePlanProposalDTO,
  RoutePlanUnplannableReasonEnum,
} from 'api/model';
import { PackageApplyState } from './PackageCard';
import {
  ApplySummary,
  getSelectedCandidate,
  PackageActionPlan,
  PackageSelection,
} from './selection-helpers';

/**
 * Allt vyn vet i ögonblicket feedbacken öppnas. Tas vid klicket på ikonen — inte när planeraren
 * trycker Skicka — så att det som mejlas är läget planeraren reagerade på, även om hon hinner
 * ändra något medan hon skriver.
 */
export interface RoutePlanFeedbackInput {
  filters: {
    date: string;
    businessUnits: { id: number; name?: string }[];
    counties: { id: number; name?: string }[];
    fieldTesters: { id: number; name?: string }[];
    employment: string;
  };
  view: {
    activeTab: string;
    sortMode: string;
    showOverview: boolean;
    generatedInfo: string | null;
    hoveredPackage: number | null;
    isGenerating: boolean;
    isApplying: boolean;
    autoPackageIndexes: number[];
    manualPackageIndexes: number[];
    collapsedPackages: Record<number, boolean>;
  };
  proposal: RoutePlanProposalDTO | null;
  selections: Record<number, PackageSelection>;
  applyStates: Record<number, PackageApplyState>;
  applySummary: ApplySummary | null;
  pendingPlans: PackageActionPlan[];
  errors: { preview?: string; apply?: string };
}

export interface RoutePlanFeedbackSnapshot {
  capturedAt: string;
  page: { url: string; userAgent: string; viewport: string };
  filterSummary: string;
  stateSummary: string[];
  /** Kort läsbar rad per paket: vald FT, markering, verkställt — så JSON:en går att skumma. */
  packageOverview: PackageOverviewRow[];
  state: RoutePlanFeedbackInput;
}

interface PackageOverviewRow {
  packageIndex: number;
  planningClass: string;
  manualReasons: string[];
  legs: string[];
  included: boolean;
  selectedFieldTester?: string;
  selectedCandidateRank?: number;
  firstGapChoice?: string;
  lastGapChoice?: string;
  hotelConfirmed?: boolean;
  applyState: string;
}

const describeList = (
  label: string,
  items: { id: number; name?: string }[],
  empty: string
) =>
  `${label}: ${items.length === 0 ? empty : items.map((i) => i.name ?? `#${i.id}`).join(', ')}`;

export const buildFilterSummary = (
  filters: RoutePlanFeedbackInput['filters']
): string =>
  [
    `Datum: ${filters.date}`,
    describeList('Affärsenhet', filters.businessUnits, 'alla'),
    describeList('Område', filters.counties, 'alla'),
    `Anställning: ${filters.employment}`,
    describeList('Fälttestare', filters.fieldTesters, 'alla'),
  ].join(' · ');

const describeApplyState = (state: PackageApplyState | undefined): string => {
  if (!state || state.status === 'idle') return 'ej verkställt';
  if (state.status === 'applied')
    return state.skipped > 0
      ? `verkställt, ${state.skipped} rader hoppades över`
      : 'verkställt';
  return `misslyckades: ${state.message}`;
};

const overviewRow = (
  pkg: RoutePackageDTO,
  selection: PackageSelection | undefined,
  applyState: PackageApplyState | undefined
): PackageOverviewRow => {
  const candidate = selection
    ? getSelectedCandidate(pkg, selection)
    : undefined;
  return {
    packageIndex: pkg.packageIndex,
    planningClass: String(pkg.planningClass),
    manualReasons: pkg.manualReasons.map(String),
    legs: pkg.legs.map(
      (leg) =>
        `${leg.registrationNumber || `uppdrag ${leg.assignmentId}`} ${leg.fromCity} → ${leg.toCity}`
    ),
    included: !!selection?.includePackage,
    selectedFieldTester: candidate?.fullName,
    selectedCandidateRank: candidate?.rank,
    firstGapChoice: selection?.firstGapChoice,
    lastGapChoice: selection?.lastGapChoice,
    hotelConfirmed: selection?.hotelConfirmed,
    applyState: describeApplyState(applyState),
  };
};

export const buildStateSummary = (input: RoutePlanFeedbackInput): string[] => {
  const { proposal, view, applyStates, applySummary, errors } = input;
  const lines: string[] = [];

  if (!proposal) {
    lines.push(
      view.isGenerating
        ? 'Förslag genereras just nu'
        : 'Inget förslag genererat ännu'
    );
  } else {
    const states = Object.values(applyStates);
    lines.push(
      `Flik: ${view.activeTab === 'auto' ? 'Redo att tillämpa' : 'Behöver input'} · sortering ${view.sortMode}`,
      `${proposal.packages.length} paket (${view.autoPackageIndexes.length} redo, ${view.manualPackageIndexes.length} behöver input), ${proposal.unplannableAssignments.filter((a) => a.reason !== RoutePlanUnplannableReasonEnum.FlexibleStock).length} oplanerbara uppdrag, ${proposal.unplannableAssignments.filter((a) => a.reason === RoutePlanUnplannableReasonEnum.FlexibleStock).length} lager att flytta, ${proposal.driverSwaps.length} skiften`,
      `Markerat för verkställan: ${applySummary?.packageCount ?? 0} paket / ${applySummary?.assignmentCount ?? 0} uppdrag`,
      `Verkställt: ${states.filter((s) => s.status === 'applied').length} paket, misslyckade: ${states.filter((s) => s.status === 'failed').length}`
    );
    if (view.generatedInfo) lines.push(view.generatedInfo);
    if (view.showOverview) lines.push('"Så togs förslaget fram" var öppen');
  }
  if (errors.preview) lines.push(`Fel vid generering: ${errors.preview}`);
  if (errors.apply) lines.push(`Fel vid verkställan: ${errors.apply}`);

  return lines;
};

export const buildFeedbackSnapshot = (
  input: RoutePlanFeedbackInput,
  now: Date = new Date()
): RoutePlanFeedbackSnapshot => ({
  capturedAt: now.toISOString(),
  page: {
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    viewport:
      typeof window !== 'undefined'
        ? `${window.innerWidth}x${window.innerHeight}`
        : '',
  },
  filterSummary: buildFilterSummary(input.filters),
  stateSummary: buildStateSummary(input),
  packageOverview: (input.proposal?.packages ?? []).map((pkg) =>
    overviewRow(
      pkg,
      input.selections[pkg.packageIndex],
      input.applyStates[pkg.packageIndex]
    )
  ),
  state: input,
});

/** Felmeddelandet ur en axios-/mutationsfel, så gott det går. */
export const describeError = (error: unknown): string | undefined => {
  if (!error) return undefined;
  const e = error as {
    message?: string;
    response?: { status?: number; data?: unknown };
  };
  const status = e.response?.status ? `HTTP ${e.response.status}` : undefined;
  const data =
    typeof e.response?.data === 'string'
      ? e.response.data
      : e.response?.data
        ? JSON.stringify(e.response.data)
        : undefined;
  return (
    [status, data ?? e.message].filter(Boolean).join(': ') || String(error)
  );
};

/**
 * Toasttexten när "Byt fälttestare" inte kunde omvärdera en fritt vald FT. En 404 bär serverns
 * klartextorsak (otillgänglig, schemat täcker inte fönstret inkl. pendling, koordinater …) och
 * visas som den är; övriga fel (403, 500, nätverk) visas med status så att de inte förväxlas med
 * ett tillgänglighetsbesked.
 */
export const describeEvaluateFieldTesterError = (error: unknown): string => {
  const e = error as
    | { response?: { status?: number; data?: unknown } }
    | undefined;
  const data = e?.response?.data;
  if (e?.response?.status === 404 && typeof data === 'string' && data.trim()) {
    return data;
  }
  return `Fälttestaren kunde inte utvärderas (${describeError(error) ?? 'okänt fel'}).`;
};
